import * as React from 'react'
import { Question } from '@phosphor-icons/react'
import { cn, neutralIconTone } from '../../lib/utils'
import { Popover, PopoverTrigger, PopoverContent } from './popover'
import { Link } from './link'

/**
 * HelpPopover — a small question-mark icon button that reveals a help blurb *on
 * click* (Radix Popover under the hood). The DS2026 replacement for the legacy
 * Tippy `HelpTooltip` used on table-header help icons: click to open (a true
 * tooltip can't click-trigger), dismiss on outside-click or Escape, keyboard
 * focusable.
 *
 *   <th>
 *     Lifetime value
 *     <HelpPopover title="Lifetime value">
 *       Projected revenue over the customer relationship.
 *     </HelpPopover>
 *   </th>
 *
 * Reuses `PopoverContent` (white panel, arrow, `size="sm"` by default) and the
 * DS `Link` for the optional trailing link. The trigger glyph is gray-500 →
 * gray-600 on hover, with a brand focus ring.
 */

export interface HelpPopoverProps {
  /** Bold heading above the body copy. */
  title?: React.ReactNode
  /** Help body — the explanation shown in the panel. */
  children: React.ReactNode
  /** Optional trailing "learn more" link. */
  link?: { href: string; label: React.ReactNode }
  /** Override the trigger glyph (defaults to an outline question mark). */
  icon?: React.ReactNode
  /** Accessible name for the trigger button. Defaults to "Help". */
  label?: string
  /** Panel size — `sm` (default, table-header help) or `md`. */
  size?: 'sm' | 'md'
  /** Trigger glyph size — `md` (default, 16px) or `sm` (14px) for dense rows
   *  like small inline stats. */
  triggerSize?: 'sm' | 'md'
  /** Placement of the panel relative to the trigger. */
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  /** Classes on the trigger button. */
  className?: string
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
}

export const HelpPopover = ({
  title,
  children,
  link,
  icon,
  label = 'Help',
  size = 'sm',
  triggerSize = 'md',
  side,
  align = 'center',
  className,
  open,
  defaultOpen,
  onOpenChange,
}: HelpPopoverProps) => (
  <Popover open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
    <PopoverTrigger asChild>
      <button
        type="button"
        aria-label={label}
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded outline-none transition-colors focus-visible:ring-2 focus-visible:ring-fbui-ring-brand data-[state=open]:bg-fbui-gray-100 data-[state=open]:text-fbui-gray-600',
          neutralIconTone,
          triggerSize === 'sm'
            ? 'size-5 [&>svg]:size-3.5'
            : 'size-6 [&>svg]:size-4',
          className
        )}
      >
        {icon ?? <Question weight="regular" aria-hidden="true" />}
      </button>
    </PopoverTrigger>
    <PopoverContent size={size} side={side} align={align}>
      {title && <p className="font-semibold text-fbui-text-title">{title}</p>}
      <div className={cn('text-fbui-text-secondary', title && 'mt-1')}>
        {children}
      </div>
      {link && (
        <Link
          href={link.href}
          target="_blank"
          rel="noreferrer"
          variant="primary"
          size="sm"
          className="mt-2"
        >
          {link.label}
        </Link>
      )}
    </PopoverContent>
  </Popover>
)
HelpPopover.displayName = 'HelpPopover'
